import React, { lazy, Suspense, useState, useCallback, useRef } from 'react';
import { LoadingSpinner } from 'components/common';
import { useSelector, useDispatch } from 'react-redux';
import { FaRegListAlt } from 'react-icons/fa';
import { FaBoxesPacking } from 'react-icons/fa6';
import { MdDashboard } from "react-icons/md";
import JobHeader from '../../../components/app/crm/shipment/jobs/JobHeader';
import { FaSyncAlt, FaDownload } from 'react-icons/fa';
import Tabs from '../../../components/common/Tabs';
import ToggleSwitch from '../../../components/common/ToggleSwitch';
import ImportPackingListDrawer from '../../../components/app/crm/shipment/packing-list/ImportPackingListDrawer';
import dayjs from 'helpers/dayjs';

const LazyJobList = lazy(() => import("../../../components/app/crm/shipment/jobs/JobList"));
const LazyJobDashboard = lazy(() => import("../../../components/app/crm/shipment/jobs/JobDashboard"));
const LazyPackingList = lazy(() => import("../../../components/app/crm/shipment/packing-list/PackingList"));

// Tab index of the "Packing list detail" tab — Import button is shown only here.
const PACKING_LIST_TAB = 2;

// Import required Redux actions
import { getShipmentForJobs } from "redux/crm/shipment/packingListSlice";

const ShipmentForJobPage = () => {
  const { loading } = useSelector((state) => state.crm_shipment_packing_list);

  // State management
  const [filters, setFilters] = useState({
    job_number: '',
    year: dayjs().year(),
    monthYear: dayjs().format('MM-YYYY'),
    chartType: 'bar'
  });
  const [viewType, setViewType] = useState('yearly_summary');
  const [activeTab, setActiveTab] = useState(0);
  const [showImport, setShowImport] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [debouncedJobNumber, setDebouncedJobNumber] = useState('');
  const gridRef = useRef(null);
  const dispatch = useDispatch();

  // Data state
  const [tableData, setTableData] = useState({ rows: [], columns: [], totalRows: 0, summary: {} });
  const [pageReady, setPageReady] = useState(false);

  // Handle year change
  const handleYearChange = useCallback((e) => {
    setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }));
  }, []);

  // Handle month change
  const handleMonthChange = useCallback((e) => {
    // Input gives YYYY-MM
    const date = dayjs(e.target.value, 'YYYY-MM');
    setFilters(prev => ({ ...prev, monthYear: date.format('MM-YYYY') }));
  }, []);

  // Handle filter changes from JobList component
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      job_number: value
    }));
  }, []);

  // Handle toggle switch change
  const handleToggleViewType = useCallback((isYearly) => {
    setViewType(isYearly ? 'yearly_summary' : 'monthly_summary');
  }, []);

  // Fetch data function - memoized with stable dependencies
  const fetchData = useCallback(async () => {
    try {
      const params = {
        view_type: viewType,
        page: 1,
        per_page: 1000
      };

      // Add year or month filter based on view type
      if (viewType === 'yearly_summary') {
        params.year = filters.year;
      } else if (viewType === 'monthly_summary') {
        params.month = filters.monthYear;
      }

      // Add job number filter if present
      if (debouncedJobNumber && debouncedJobNumber.trim()) {
        params.filters = { job_number: debouncedJobNumber };
      }

      const response = await dispatch(getShipmentForJobs(params)).unwrap();
      const data = response?.data || response || {};

      setTableData({
        rows: Array.isArray(data.rows) ? data.rows : [],
        columns: Array.isArray(data.columns) ? data.columns : [],
        totalRows: data.total_rows || 0,
        summary: data.summary || {}
      });
    } catch (error) {
      console.error("Error fetching shipment jobs:", error);
    }
  }, [viewType, filters.year, filters.monthYear, debouncedJobNumber, dispatch]);

  React.useEffect(() => { setPageReady(true); }, []);

  // Debounce job number updates
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedJobNumber(filters.job_number);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.job_number]);

  // Trigger fetch when filters or viewType change
  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Export functionality - create CSV from table data
  const handleExport = useCallback(() => {
    try {
      if (!tableData.rows || tableData.rows.length === 0) {
        alert('No data to export');
        return;
      }

      // Get column headers
      const headers = tableData.columns
        .map(col => col.headerName || col.field)
        .join(',');

      // Convert rows to CSV
      const csvRows = tableData.rows.map(row => {
        return tableData.columns
          .map(col => {
            const value = row[col.field] || '';
            // Escape quotes and wrap in quotes if contains comma
            const stringValue = String(value).replace(/"/g, '""');
            return `"${stringValue}"`;
          })
          .join(',');
      });

      // Combine with summary row if available
      let csvContent = headers + '\n' + csvRows.join('\n');

      if (tableData.summary && Object.keys(tableData.summary).length > 0) {
        const summaryRow = tableData.columns
          .map(col => {
            const value = tableData.summary[col.field] || '';
            const stringValue = String(value).replace(/"/g, '""');
            return `"${stringValue}"`;
          })
          .join(',');
        csvContent += '\n' + summaryRow;
      }

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const fileName = `shipment-jobs-${viewType}-${filters.year || dayjs().year()}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data');
    }
  }, [tableData, viewType, filters.year]);

  // Refresh data handler
  const handleRefresh = useCallback(() => {
    fetchData();
    // Trigger child component refresh by incrementing counter
    setRefreshTrigger(prev => prev + 1);
  }, [fetchData]);

  // Memoize years array
  const yearOptions = React.useMemo(
    () => Array.from({ length: 5 }, (_, i) => dayjs().year() - 2 + i),
    []
  );

  if (!pageReady) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50 tw-gap-4">
        <LoadingSpinner size="lg" />
        <p className="tw-text-sm tw-text-gray-500 tw-animate-pulse">Loading Shipment for Jobs…</p>
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-h-full tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <JobHeader onImport={() => setShowImport(true)} />

      {/* Controls Bar — jobs filters on Dashboard/Detail; hidden on the Packing
          list tab (that tab has its own filter toolbar). */}
      {activeTab !== PACKING_LIST_TAB && (
      <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-2 tw-bg-white tw-rounded tw-p-3 tw-shadow-sm">
        {/* Search Input */}
        <div className="tw-flex tw-gap-0 tw-border tw-border-gray-300 tw-rounded-md tw-overflow-hidden lg:tw-flex-1">
          <input
            type="text"
            placeholder="Search job number..."
            value={filters.job_number}
            onChange={handleSearchChange}
            className="tw-border-0 tw-text-sm tw-flex-1 tw-px-3 tw-py-2 tw-h-10 tw-bg-white tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-inset"
          />
        </div>
        
        {/* View Type & Date Filters */}
        <div className="tw-flex tw-gap-2 tw-flex-1">
          {/* View Type Toggle */}
          <ToggleSwitch
            value={viewType === 'yearly_summary'}
            onChange={handleToggleViewType}
            labelLeft="Monthly"
            labelRight="Yearly"
            className="tw-flex-shrink-0"
          />

          {viewType === 'monthly_summary' ? (
            <input
              type="month"
              className="tw-text-sm tw-h-10 tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-bg-white tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-inset tw-flex-1"
              value={dayjs(filters.monthYear, 'MM-YYYY').format('YYYY-MM')}
              onChange={handleMonthChange}
            />
          ) : (
            <select
              className="tw-text-sm tw-h-10 tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-bg-white tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-inset tw-flex-1"
              value={filters.year}
              onChange={handleYearChange}
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="tw-flex tw-gap-2">
          <button onClick={handleRefresh} disabled={loading} title="Refresh" className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-px-3 tw-h-10 tw-text-sm tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 disabled:tw-opacity-50">
            <FaSyncAlt size={14} />
            <span className="tw-hidden sm:tw-inline">Refresh</span>
          </button>
          <button onClick={handleExport} disabled={loading} title="Export to CSV" className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-px-3 tw-h-10 tw-text-sm tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 disabled:tw-opacity-50">
            <FaDownload size={14} />
            <span className="tw-hidden sm:tw-inline">Export</span>
          </button>
        </div>
      </div>
      )}

      {/* Loading Indicator */}
      {loading && activeTab !== PACKING_LIST_TAB && <LoadingSpinner label="Loading shipment data..." />}

      {/* Main Content */}
      <div className="tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm tw-overflow-hidden">
        <Tabs
          variant="underline"
          defaultTab={activeTab}
          onChange={setActiveTab}
          navClassName="tw-px-3 tw-pt-1"
          contentClassName="tw-bg-gray-50 tw-p-4"
          className="tw-w-full"
          tabs={[
            {
              label: 'Dashboard',
              icon: <MdDashboard size={18} />,
              content: (
                <Suspense fallback={<LoadingSpinner />}>
                  <LazyJobDashboard
                    filters={filters}
                    viewType={viewType}
                    gridRef={gridRef}
                    data={tableData}
                  />
                </Suspense>
              ),
            },
            {
              label: 'Detail',
              icon: <FaRegListAlt size={16} />,
              content: (
                <Suspense fallback={<LoadingSpinner />}>
                  <LazyJobList
                    filters={filters}
                    viewType={viewType}
                    onFilterChange={handleFilterChange}
                    gridRef={gridRef}
                    showHeader={false}
                    showDashboard={false}
                    showCharts={false}
                    showTable={true}
                    data={tableData}
                    refreshTrigger={refreshTrigger}
                  />
                </Suspense>
              ),
            },
            {
              label: 'Packing list detail',
              icon: <FaBoxesPacking size={16} />,
              content: (
                <Suspense fallback={<LoadingSpinner />}>
                  <LazyPackingList />
                </Suspense>
              ),
            },
          ]}
        />
      </div>

      {/* Import packing list drawer (opened from the header on the Packing list tab) */}
      <ImportPackingListDrawer show={showImport} onHide={() => setShowImport(false)} />
    </div>
  );
};

export default ShipmentForJobPage;